import { type PropsWithChildren, useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { IgTypography } from "@intelligems/ig-theme";
import { Box, Stack } from "@mui/material";
import * as Sentry from "@sentry/react";
import { usePrefetchPendingExperiments } from "@src/api/experiences/use-prefetch-pending-experiments";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { EXPERIMENT_ROUTES } from "@src/routes/routes";
import { useDispatch } from "@src/store";
import { appendNewOrg } from "@src/store/slices/appSettings";
import { initOrganization } from "@src/store/slices/organization";

const MobileLoggedInProvider = ({ children }: PropsWithChildren) => {
  void usePrefetchPendingExperiments();
  // TODO: when we add personalizations
  // void usePrefetchPendingPersonalizations
  const dispatch = useDispatch();
  const organization = useGetOrg();
  const location = useLocation();

  // Track visited organizations
  useEffect(() => {
    if (
      organization.data.org.shopId !== null &&
      organization.data.org.name !== null
    ) {
      dispatch(
        appendNewOrg({
          shopId: organization.data.org.shopId!,
          name: organization.data.org.name,
        })
      );
    }
  }, [dispatch, organization.data.org.shopId, organization.data.org.name]);

  // Initialize organization in Redux store
  useEffect(() => {
    dispatch(initOrganization(organization.data.org));
  }, [dispatch, organization.data.org]);

  if (location.pathname === "/") {
    return <Navigate replace={true} to={EXPERIMENT_ROUTES.Experiments} />;
  }

  return (
    <Box
      id="ig-mobile-layout-root"
      sx={{ position: "relative", width: "100vw", height: "100svh" }}
    >
      {children || <Outlet />}
    </Box>
  );
};

const getErrorMsg = (err: unknown): string => {
  if (err == null) {
    return "Unknown error";
  }

  if (typeof err === "string") {
    return err;
  }

  if (err instanceof Error) {
    if (err.stack) {
      return err.stack;
    }
    return err.message;
  }

  return "VERY Unknown error";
};

const prodHost = "app.intelligems.io";
const Wrapped = Sentry.withErrorBoundary(MobileLoggedInProvider, {
  fallback: (props) => {
    const isProd = window.location.hostname === prodHost;
    const errMsgStr = getErrorMsg(props.error);
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{
          width: "100vw",
          height: "100svh",
        }}
      >
        <IgTypography variant={"h3"}>Oops! Something went wrong!</IgTypography>
        {!isProd && (
          <Box
            component="pre"
            sx={{
              overflow: "auto",
              borderRadius: 1,
              bgcolor: "grey.100",
              p: 2,
              fontSize: "0.875rem",
              m: 0,
            }}
          >
            <Box
              component="code"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "monospace",
              }}
            >
              {errMsgStr}
            </Box>
          </Box>
        )}
      </Stack>
    );
  },
});

export default Wrapped;
